import { Component, inject, Input, OnInit } from '@angular/core';
import { BaseLoadComponent } from '@shared/components/classes/base-load.component';
import { Resources } from '@app/resources';
import { Observable } from 'rxjs';
import { Property } from '@modules/universal-billing/properties/properties.interface';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { Apartment } from '@modules/universal-billing/apartments/apartments.interface';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ConfigurationProvidersComponent } from './tabs/configuration-providers/configuration-providers.component';
import { ObjectResposibleComponent } from './tabs/object-resposible/object-resposible.component';
import { PersonalPropertyInfoComponent } from './personal-property-info/personal-property-info.component';
import { ModalClose } from '@shared/services/modal/modal.enum';
import { ModalService } from '@shared/services/modal/modal.service';
import { ApartmentsComponent } from '@modules/universal-billing/apartments/apartments.component';
import { PropertyMaintenanceComponent } from './tabs/property-maintenance/property-maintenance.component';
import { PropertyEnergyComponent } from './tabs/property-energy/property-energy.component';
import { PropertiesService } from '@modules/settings/properties/properties.service';
import { CommercialComponent } from './tabs/property-units/commercial/commercial.component';
import { ResidentialComponent } from './tabs/property-units/residential/residential.component';
import { PropertyApartmentsComponent } from './tabs/property-apartments/property-apartments.component';
import { ContractsComponent } from '@modules/universal-billing/property-management/contracts/contracts.component';
import { takeUntil } from "rxjs/operators";
import {
  ApartmentInvoicesComponent
} from "@modules/universal-billing/apartments/apartment-profile/tabs/apartment-invoices/apartment-invoices.component";
import {
  ApartmentPaymentsComponent
} from "@modules/universal-billing/apartments/apartment-profile/tabs/apartment-payments/apartment-payments.component";
import {
  ApartmentReadingsComponent
} from "@modules/universal-billing/apartments/apartment-profile/tabs/apartment-readings/apartment-readings.component";
import { CardComponent } from '@dashboard/card/card.component';
import { CustomNumberPipe } from '@common/pipe/custom-number.pipe';
import { DocumentsComponent } from '@modules/universal-billing/accounting/documents/documents.component';
import { MetersComponent } from '@modules/universal-billing/meters/meters.component';
import {AuthService} from '@app/auth/auth.service';
import {Roles} from '@common/rbac/roles';
import {NgCanRoleDirective} from '@common/rbac/ng-can-role.directive';
import { NgCanDirective } from '@common/rbac/ng-can.directive';

export enum TabId {
  UNITS = 'units',
  CONTRACTS = 'contracts',
  MAINTENANCE = 'maintenance',
  ENERGY = 'energy',
  OBJECTDATA = 'objectdata',
  CONFIGURATION = 'configuration',
  COUNTER = 'counter',
  DOCUMENT='document'
}


interface Tab {
  id: TabId;
  titleKey: string;
  iconClass: string;
  permission?: string;
}

@Component({
  selector: 'app-building-profile',
  standalone: true,
  imports: [
    CommonModule,
    PageTitleComponent,
    RouterLink,
    TranslatePipe,
    NgxSkeletonLoaderComponent,
    NgbTooltip,
    ObjectResposibleComponent,
    PersonalPropertyInfoComponent,
    ApartmentsComponent,
    PropertyMaintenanceComponent,
    PropertyEnergyComponent,
    CommercialComponent,
    ResidentialComponent,
    PropertyApartmentsComponent,
    ConfigurationProvidersComponent,
    ContractsComponent,
    ApartmentInvoicesComponent,
    ApartmentPaymentsComponent,
    ApartmentReadingsComponent,
    CardComponent,
    CustomNumberPipe,
    DocumentsComponent,
    MetersComponent,
    NgCanRoleDirective,
    NgCanDirective
  ],
  templateUrl: './property-profile.component.html',
  styleUrl: './property-profile.component.scss'
})
export class PropertyProfileComponent extends BaseLoadComponent<Property> implements OnInit {
  @Input() id: number;

  public breadCrumbItems = [];
  public isVacant: string | null = null;

  public tabs: Tab[] = [
    { id: TabId.UNITS, titleKey: 'PROPERTY.TAB.UNITS', iconClass: 'la la-home', permission: 'apartments.index'},
    { id: TabId.CONTRACTS, titleKey: 'PROPERTY.TAB.CONTRACTS', iconClass: 'la la-file-contract', permission: 'rental-contracts.index'},
    { id: TabId.OBJECTDATA, titleKey: 'PROPERTY.OBJECT_DATA', iconClass: 'la la-user', permission: ''},
    { id: TabId.CONFIGURATION, titleKey: 'PROPERTY.TAB.CONFIGURATION', iconClass: 'las la-cog', permission: 'utility-provider-configurations.get'},
    { id: TabId.COUNTER, titleKey: 'PROPERTY.TAB.COUNTER', iconClass: 'las la-tachometer-alt', permission: 'meters.index'},
    { id: TabId.DOCUMENT, titleKey: 'MAIN.DOCUMENTS', iconClass: 'las la-file',permission: 'documents.index' }
  ];

  public activeTab!: TabId;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected modalService = inject(ModalService);
  protected propertiesService = inject(PropertiesService);
  protected translate = inject(TranslateService);
  authService = inject(AuthService);

  ngOnInit(): void {
    this.tabs = this.tabs.filter(tab => !tab.permission || this.authService.ngCan(tab.permission));

    const frag = this.route.snapshot.fragment as keyof typeof TabId;
    this.updateActiveTab(frag);

    this.route.fragment
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(frag => {
        this.updateActiveTab(frag);
      });

    this.breadCrumbItems = [
      { label: 'MENU.UNIVERSAL_BILLING' },
      { label: this.propertiesService.title, link: Resources.Properties },
      { label: this.id, active: true }
    ];
    super.ngOnInit()
  }
  updateActiveTab(frag: string) {
    const findTab = this.tabs.find(tab => tab.id == frag);
    this.activeTab = findTab ? findTab.id : (this.tabs.length > 0 ? this.tabs[0].id : null);
  }
  getData(): Observable<any> {
    return this.propertiesService.getOne(this.id);
  }

  // afterLoadData(data: Property) {
  //   this.groupApartments();
  // }

  selectTab(tab: Tab): void {
    this.router.navigate([], {
      fragment: tab.id,
      replaceUrl: true,
      queryParamsHandling: 'preserve'
    });
  }
  //
  // groupApartments() {
  //   this.groupedApartments = {};
  //   this.hasAnyVacantBlock = {};
  //   this.hasAnyVacantFloor = {};
  //   this.allCount = 0;
  //   this.vacantCount = 0;
  //   this.occupiedCount = 0;
  //   this.data().apartments.forEach(apartment => {
  //     this.allCount++;
  //     if (apartment.isVacant == ApartmentStatus.VACANT) {
  //       this.vacantCount++;
  //     } else {
  //       this.occupiedCount++;
  //     }
  //
  //     if (this.isVacant && apartment.isVacant != this.isVacant) return;
  //
  //     if (!this.hasAnyVacantBlock[apartment.floor]) {
  //       this.hasAnyVacantBlock[apartment.floor] = {};
  //     }
  //
  //     if (apartment.isVacant == ApartmentStatus.VACANT) {
  //       this.hasAnyVacantBlock[apartment.floor][apartment.block] = 1;
  //       this.hasAnyVacantFloor[apartment.floor] = 1;
  //     }
  //
  //     if (!this.groupedApartments[apartment.floor]) {
  //       this.groupedApartments[apartment.floor] = {};
  //     }
  //
  //     if (!this.groupedApartments[apartment.floor][apartment.block]) {
  //       this.groupedApartments[apartment.floor][apartment.block] = [];
  //     }
  //
  //     this.groupedApartments[apartment.floor][apartment.block].push(apartment);
  //   });
  // }

  update() {
    this.modalService.open({
      component: this.propertiesService.formComponent,
      title: 'PROPERTY.EDIT_PROPERTY',
      componentOptions: {
        selectedRow: this.data(),
        isEdit: true,
        _defaultService: this.propertiesService
      },
      modalOptions: this.propertiesService?.formModalOptions ?? {}
    }).then((res) => {
      if (res == ModalClose.SUCCESS) {
        this.loadData();
      }
    }).catch((err) => {})
  }

  protected readonly TabId = TabId;
  protected readonly Roles = Roles;
}
